import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { IconeSeta } from '../Icones.js';

export type DirecaoLista = 'asc' | 'desc';
export interface OrdemLista {
  col: string;
  dir: DirecaoLista;
}

export interface ColunaLista<L> {
  id: string;
  rotulo: string;
  /** Rotulo menor para o cartao do celular, onde a celula e estreita. */
  rotuloCurto?: string;
  /** Trilho da grade no desktop, ex.: '120px'. */
  largura: string;
  celula: (l: L) => ReactNode;
  /** Valor da barrinha quando a lista esta ordenada por esta coluna; null = sem barra. */
  medida?: (l: L) => number | null;
  ordenavel?: boolean;
  /** Direcao do primeiro clique: numero grande desce, custo e texto sobem. */
  dirPadrao?: DirecaoLista;
  /** Coluna de texto: a direcao se le "A–Z", nao "crescente". */
  texto?: boolean;
}

/**
 * Lista de itens no estilo "uma linha por peca": o que identifica fica junto a
 * esquerda (`principal`), os numeros que decidem ficam a direita, e o resto
 * mora no detalhe, que abre no lugar sem perder a posicao na lista.
 *
 * - A lista ja chega filtrada e ordenada (funcao pura da pagina); aqui so se
 *   mostra. Mude a `key` quando o recorte mudar, para a janela voltar ao inicio.
 * - A barrinha aparece so na coluna que ordena, na escala do recorte INTEIRO
 *   (nao da janela): "mostrar mais" nao reescala linhas que nao mudaram.
 * - Ordena pelos titulos (desktop) ou pelo "Ordenar por" (todos, e o unico no
 *   celular, onde os titulos somem e os numeros viram cartoes com rotulo).
 * - Expandir e um botao com aria-expanded; o nome dentro de `principal` deve
 *   ser um link de verdade para a pagina do item.
 */
export function Lista<L>({
  titulo,
  dica,
  linhas,
  chave,
  rotuloItem,
  principal,
  rotuloPrincipal,
  ordemPrincipal,
  colunas,
  ordem,
  onOrdenar,
  detalhe,
  fraca,
  acao,
  passo = 100,
  vazio,
}: {
  titulo: ReactNode;
  dica?: ReactNode;
  linhas: readonly L[];
  chave: (l: L) => string;
  /** Nome do item, para os rotulos acessiveis ("Abrir detalhe de ..."). */
  rotuloItem: (l: L) => string;
  principal: (l: L) => ReactNode;
  rotuloPrincipal: string;
  /** Ordenacao pelo principal (ex.: nome), se houver. */
  ordemPrincipal?: { id: string; rotulo: string; texto?: boolean; dirPadrao?: DirecaoLista };
  colunas: readonly ColunaLista<L>[];
  ordem: OrdemLista;
  onOrdenar: (o: OrdemLista) => void;
  detalhe?: (l: L) => ReactNode;
  fraca?: (l: L) => boolean;
  /** Botao/link a direita da linha (ex.: abrir a ficha). */
  acao?: (l: L) => ReactNode;
  passo?: number;
  vazio?: ReactNode;
}) {
  const [janela, setJanela] = useState(passo);
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set());
  const base = useId();

  const opcoesOrdem = [
    ...(ordemPrincipal
      ? [
          {
            id: ordemPrincipal.id,
            rotulo: ordemPrincipal.rotulo,
            dirPadrao: ordemPrincipal.dirPadrao ?? ('asc' as const),
            texto: ordemPrincipal.texto ?? true,
          },
        ]
      : []),
    ...colunas
      .filter((c) => c.ordenavel !== false)
      .map((c) => ({ id: c.id, rotulo: c.rotulo, dirPadrao: c.dirPadrao ?? ('desc' as const), texto: !!c.texto })),
  ];
  const escolher = (id: string, alternar: boolean) => {
    const o = opcoesOrdem.find((x) => x.id === id);
    if (!o) return;
    onOrdenar(
      alternar && ordem.col === id
        ? { col: id, dir: ordem.dir === 'asc' ? 'desc' : 'asc' }
        : { col: id, dir: o.dirPadrao },
    );
  };

  const colunaOrdem = colunas.find((c) => c.id === ordem.col);
  const maximo = colunaOrdem?.medida ? Math.max(0, ...linhas.map((l) => colunaOrdem.medida!(l) ?? 0)) : 0;
  const temAcoes = !!detalhe || !!acao;
  const larguraAcoes = detalhe && acao ? '72px' : temAcoes ? '32px' : '';
  const grade = {
    '--colunas': ['minmax(0,1fr)', ...colunas.map((c) => c.largura), larguraAcoes].filter(Boolean).join(' '),
    '--colunas-celular': `repeat(${colunas.length}, minmax(0,1fr))`,
  } as CSSProperties;
  const visiveis = linhas.slice(0, janela);
  const textoOrdem = opcoesOrdem.find((o) => o.id === ordem.col);

  const alternarAberta = (k: string) =>
    setAbertas((a) => {
      const n = new Set(a);
      if (!n.delete(k)) n.add(k);
      return n;
    });

  return (
    <section className="overflow-hidden rounded-(--radius-painel) border border-borda bg-superficie shadow-cartao">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold">{titulo}</h2>
          {dica && <p className="mt-0.5 text-xs text-apagado">{dica}</p>}
        </div>
        {opcoesOrdem.length > 0 && (
          <div className="flex items-center gap-2 max-sm:w-full">
            <label htmlFor={`${base}-ordem`} className="text-xs whitespace-nowrap text-apagado max-sm:sr-only">
              Ordenar por
            </label>
            <span className="relative max-sm:flex-1">
              <select
                id={`${base}-ordem`}
                value={ordem.col}
                onChange={(e) => escolher(e.target.value, false)}
                className="w-full appearance-none rounded-(--radius-controle) border border-borda bg-superficie py-2 pr-8 pl-3 text-[13px] font-medium hover:border-borda-forte sm:min-w-44"
              >
                {opcoesOrdem.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.rotulo}
                  </option>
                ))}
              </select>
              <IconeSeta
                width={14}
                height={14}
                className="pointer-events-none absolute top-1/2 right-2.5 -translate-y-1/2 text-apagado"
              />
            </span>
            <button
              type="button"
              onClick={() => onOrdenar({ col: ordem.col, dir: ordem.dir === 'asc' ? 'desc' : 'asc' })}
              title="Inverter a ordem"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-(--radius-controle) border border-borda bg-superficie px-3 text-xs font-semibold whitespace-nowrap hover:border-borda-forte"
            >
              <IconeSeta direcao={ordem.dir === 'asc' ? 'cima' : 'baixo'} width={14} height={14} />
              {textoOrdem?.texto
                ? ordem.dir === 'asc'
                  ? 'A–Z'
                  : 'Z–A'
                : ordem.dir === 'asc'
                  ? 'Crescente'
                  : 'Decrescente'}
            </button>
          </div>
        )}
      </header>

      <div className="border-t border-borda">
        {linhas.length > 0 && (
          <div
            style={grade}
            className="hidden items-center gap-4 border-b border-borda bg-superficie-2 px-5 py-2.5 text-[10.5px] font-semibold tracking-[0.07em] text-apagado uppercase md:grid md:grid-cols-(--colunas)"
          >
            <Titulo
              rotulo={rotuloPrincipal}
              id={ordemPrincipal?.id}
              ordem={ordem}
              onClique={(id) => escolher(id, true)}
            />
            {colunas.map((c) => (
              <Titulo
                key={c.id}
                rotulo={c.rotulo}
                id={c.ordenavel === false ? undefined : c.id}
                ordem={ordem}
                onClique={(id) => escolher(id, true)}
                direita
              />
            ))}
            {temAcoes && <span />}
          </div>
        )}

        {linhas.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm leading-relaxed text-apagado">
            {vazio ?? 'Nada para mostrar.'}
          </div>
        ) : (
          <ul>
            {visiveis.map((l) => {
              const k = chave(l);
              const aberta = abertas.has(k);
              const apagada = fraca?.(l) ?? false;
              const idDetalhe = `${base}-${k}`;
              return (
                <li
                  key={k}
                  className={`border-b border-borda/70 last:border-b-0 ${aberta ? 'bg-superficie-2' : ''} ${apagada ? 'text-apagado' : ''}`}
                >
                  <div
                    style={grade}
                    className="relative grid grid-cols-(--colunas-celular) gap-x-2 gap-y-3 px-4 py-3 transition-colors hover:bg-superficie-2/70 md:grid-cols-(--colunas) md:items-center md:gap-x-4 md:px-5"
                  >
                    <div className={`min-w-0 max-md:col-span-full ${temAcoes ? 'max-md:pr-20' : ''}`}>
                      {principal(l)}
                    </div>
                    {colunas.map((c) => {
                      const m = c.id === ordem.col && maximo > 0 ? (c.medida?.(l) ?? null) : null;
                      return (
                        <div
                          key={c.id}
                          className={`min-w-0 md:text-right max-md:rounded-[10px] max-md:px-2.5 max-md:py-2 ${
                            aberta ? 'max-md:bg-superficie' : 'max-md:bg-superficie-2'
                          }`}
                        >
                          <span className="block text-[10px] tracking-[0.05em] text-apagado uppercase md:hidden">
                            {c.rotuloCurto ?? c.rotulo}
                          </span>
                          <span
                            className={`numero block leading-snug md:truncate text-[13.5px] ${apagada ? 'font-medium' : 'font-semibold text-texto'}`}
                          >
                            {c.celula(l)}
                          </span>
                          {m != null && (
                            <span
                              aria-hidden
                              className="mt-1.5 block h-1 max-w-[120px] overflow-hidden rounded-full bg-trilha md:ml-auto"
                            >
                              <span
                                className="block h-full rounded-full bg-serie"
                                style={{ width: `${Math.max(2, (m / maximo) * 100).toFixed(1)}%` }}
                              />
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {temAcoes && (
                      <div className="flex items-center justify-end gap-1.5 max-md:absolute max-md:top-3 max-md:right-4">
                        {acao?.(l)}
                        {detalhe && (
                          <button
                            type="button"
                            aria-expanded={aberta}
                            aria-controls={idDetalhe}
                            aria-label={`${aberta ? 'Fechar' : 'Abrir'} detalhe de ${rotuloItem(l)}`}
                            title={aberta ? 'Fechar detalhe' : 'Abrir detalhe'}
                            onClick={() => alternarAberta(k)}
                            className="grid size-8 min-h-8 shrink-0 place-items-center rounded-[9px] border border-borda bg-superficie text-apagado hover:border-borda-forte hover:text-destaque-texto pointer-coarse:size-11"
                          >
                            <IconeSeta
                              width={16}
                              height={16}
                              className={`transition-transform ${aberta ? 'rotate-180' : ''}`}
                            />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  {detalhe && aberta && (
                    <div
                      id={idDetalhe}
                      role="region"
                      aria-label={`Detalhe de ${rotuloItem(l)}`}
                      className="px-4 pb-4 md:px-5"
                    >
                      <div className="rounded-[12px] border border-borda bg-superficie p-4">{detalhe(l)}</div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {linhas.length > janela && (
        <footer className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t border-borda px-4 py-3.5 text-[13px] text-apagado">
          <span className="numero">
            Mostrando {visiveis.length} de {linhas.length}
          </span>
          <button
            type="button"
            onClick={() => setJanela((j) => j + passo)}
            className="font-semibold text-destaque-texto underline underline-offset-2"
          >
            Mostrar mais {Math.min(passo, linhas.length - janela)}
          </button>
          <button
            type="button"
            onClick={() => setJanela(Infinity)}
            className="font-semibold text-destaque-texto underline underline-offset-2"
          >
            Mostrar todas
          </button>
        </footer>
      )}
    </section>
  );
}

/** Titulo de coluna: botao de ordenar quando a coluna ordena, texto quando nao. */
function Titulo({
  rotulo,
  id,
  ordem,
  onClique,
  direita = false,
}: {
  rotulo: string;
  id: string | undefined;
  ordem: OrdemLista;
  onClique: (id: string) => void;
  direita?: boolean;
}) {
  if (!id) return <span className={direita ? 'text-right' : ''}>{rotulo}</span>;
  const ativo = ordem.col === id;
  return (
    <button
      type="button"
      onClick={() => onClique(id)}
      aria-label={`Ordenar por ${rotulo}${ativo ? (ordem.dir === 'asc' ? ', ordem crescente agora' : ', ordem decrescente agora') : ''}`}
      className={`flex min-h-0 items-center gap-1 tracking-[inherit] uppercase hover:text-texto ${direita ? 'justify-end' : ''} ${
        ativo ? 'text-destaque-texto' : ''
      }`}
    >
      {rotulo}
      <span aria-hidden className={ativo ? '' : 'opacity-40'}>
        {ativo ? (ordem.dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
}
